import { Link } from 'react-router-dom';

export function PrivacyPolicy() {
  return (
    <div className="h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-gray-100 overflow-y-auto">
      <div className="max-w-4xl mx-auto px-4 py-16">
        <Link 
          to="/"
          className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-8 transition-colors"
        >
          ← Back to Home
        </Link>
        
        <h1 className="text-4xl font-bold mb-8 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
          Privacy Policy
        </h1>
        
        <div className="prose prose-invert max-w-none space-y-6 text-gray-300">
          <p className="text-sm text-gray-400">Last updated: December 12, 2025</p>
          
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Introduction</h2>
            <p>
              Welcome to instadrop.io. We respect your privacy and are committed to protecting your personal data.
              This privacy policy will inform you about how we handle your personal data when you use our peer-to-peer 
              file sharing service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Information We Collect</h2>
            <p>We collect and process the following types of information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Account Information:</strong> When you create an account, we collect your email address 
                and authentication credentials.
              </li>
              <li>
                <strong>Usage Data:</strong> We collect information about how you use our service, including 
                file transfer metadata (file names, sizes, timestamps) but not the actual file contents.
              </li>
              <li>
                <strong>Technical Data:</strong> We automatically collect certain technical information including 
                IP addresses, browser type, device information, and session data.
              </li>
              <li>
                <strong>Payment Information:</strong> When you subscribe to premium features, payment processing 
                is handled by our third-party payment processor (Lemon Squeezy), and we receive only transaction 
                confirmations.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">How We Use Your Information</h2>
            <p>We use your information for the following purposes:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>To provide and maintain our file sharing service</li>
              <li>To manage your account and subscription</li>
              <li>To improve our service and develop new features</li>
              <li>To communicate with you about service updates and support</li>
              <li>To ensure security and prevent fraud</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Data Security and Peer-to-Peer Transfer</h2>
            <p>
              <strong>Important:</strong> Our service uses direct peer-to-peer (P2P) file transfer technology. 
              This means:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Files are transferred directly between users without passing through our servers</li>
              <li>We do not store, access, or have copies of the files you share</li>
              <li>File transfers are end-to-end encrypted using WebRTC technology</li>
              <li>Only connection metadata (not file contents) is stored for service functionality</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Data Sharing and Third Parties</h2>
            <p>We may share your information with:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Service Providers:</strong> Third-party companies that help us operate our service 
                (e.g., hosting providers, authentication services, payment processors)
              </li>
              <li>
                <strong>Legal Requirements:</strong> When required by law, court order, or to protect our 
                legal rights
              </li>
              <li>
                <strong>Business Transfers:</strong> In connection with any merger, sale, or acquisition of 
                our business
              </li>
            </ul>
            <p className="mt-4">
              We do not sell your personal data to third parties for their marketing purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Data Retention</h2>
            <p>
              We retain your personal data only for as long as necessary to provide our services and fulfill 
              the purposes outlined in this policy. Account information is retained until you request deletion. 
              File transfer metadata may be retained for up to 90 days for service improvement purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Your Rights</h2>
            <p>Depending on your location, you may have the following rights:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access your personal data</li>
              <li>Correct inaccurate or incomplete data</li>
              <li>Request deletion of your data</li>
              <li>Object to or restrict processing of your data</li>
              <li>Data portability</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, please contact us at the email address provided below.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Cookies and Tracking</h2>
            <p>
              We use essential cookies and local storage to maintain your session and remember your preferences. 
              We do not use third-party advertising or tracking cookies. You can control cookie settings through 
              your browser preferences.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">International Data Transfers</h2>
            <p>
              Your data may be processed in countries other than your own. We ensure appropriate safeguards 
              are in place to protect your data in accordance with this privacy policy and applicable laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Children's Privacy</h2>
            <p>
              Our service is not intended for users under 13 years of age. We do not knowingly collect 
              personal information from children under 13. If you become aware that a child has provided 
              us with personal data, please contact us.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Changes to This Policy</h2>
            <p>
              We may update this privacy policy from time to time. We will notify you of any changes by 
              posting the new policy on this page and updating the "Last updated" date. We encourage you 
              to review this policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Contact Us</h2>
            <p>
              If you have any questions about this privacy policy or our data practices, please contact us at:
            </p>
            <p className="mt-4">
              Email: <a href="mailto:hi@instadrop.io" className="text-blue-400 hover:text-blue-300">privacy@instadrop.io</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
